import { useMemo } from 'react';

/**
 * Custom hook to process and prepare chart data from API responses
 * @param {Object} rawResponse - The raw API response containing data
 * @param {boolean} hasTableResults - Whether the response has table results
 * @param {boolean} showVisualizeData - Whether visualization is enabled
 * @returns {Object} Processed chart data and metadata
 */
export const useChartData = (rawResponse, hasTableResults, showVisualizeData) => {
  const chartType = useMemo(() => {
    const interpretation = rawResponse?.interpretation || rawResponse;
    return interpretation?.suggestedChart;
  }, [rawResponse]);

  const hasChartData = useMemo(() => {
    return hasTableResults &&
      rawResponse?.results?.rows?.length > 0 &&
      chartType !== null &&
      ((rawResponse?.interpretation?.dimensions || rawResponse?.dimensions) ||
        (chartType === 'map' && (rawResponse?.interpretation?.map || rawResponse?.map)));
  }, [hasTableResults, rawResponse, chartType]);

  const chartData = useMemo(() => {
    if (!hasChartData || !showVisualizeData) {
      return null;
    }

    const interpretation = rawResponse?.interpretation || rawResponse;

    // Special case for timeseries
    if (interpretation?.suggestedChart === 'timeseries' && rawResponse?.results?.rows?.length > 0) {
      try {
        const { xaxisColumn, xaxisLabel, yaxisColumn, yaxisLabel } = interpretation.dimensions || {};

        if (!xaxisColumn || !yaxisColumn) {
          return null;
        }

        // Extract data - handle both array and object formats
        let xValues, yValues;

        if (Array.isArray(rawResponse.results.rows) && Array.isArray(rawResponse.results.rows[0])) {
          // Array of arrays format
          const columnIndices = {};
          rawResponse.results.columns.forEach((col, idx) => {
            columnIndices[col] = idx;
          });

          const xIdx = columnIndices[xaxisColumn];
          const yIdx = columnIndices[yaxisColumn];

          if (xIdx === undefined || yIdx === undefined) {
            return null;
          }

          xValues = rawResponse.results.rows.map(row => row[xIdx]);
          yValues = rawResponse.results.rows.map(row => {
            const val = row[yIdx];
            return typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val;
          });
        } else {
          // Object format
          xValues = rawResponse.results.rows.map(row => row[xaxisColumn]);
          yValues = rawResponse.results.rows.map(row => {
            const val = row[yaxisColumn];
            return typeof val === 'string' ? parseFloat(val.replace(/,/g, '')) : val;
          });
        }

        // Sort by date
        const paired = xValues.map((x, i) => ({ x, y: yValues[i] }));
        paired.sort((a, b) => new Date(a.x) - new Date(b.x));

        const sortedX = paired.map(p => p.x);
        const sortedY = paired.map(p => p.y);

        return {
          labels: sortedX,
          datasets: [{
            label: yaxisLabel || yaxisColumn,
            data: sortedY,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1
          }],
          xaxisLabel: xaxisLabel || xaxisColumn,
          yaxisLabel: yaxisLabel || yaxisColumn,
          suggestedChart: 'timeseries'
        };
      } catch (error) {
        console.error('Error generating timeseries chart data:', error);
        return null;
      }
    }

    // Handle cases with interpretation dimensions
    if (interpretation?.dimensions) {
      const { xaxisColumn, xaxisLabel, yaxisColumn, yaxisLabel } = interpretation.dimensions;

      if (!xaxisColumn || !yaxisColumn || !rawResponse?.results?.rows?.length) {
        return null;
      }

      try {
        const yValues = rawResponse.results.rows.map(item => {
          const value = item[yaxisColumn];
          if (typeof value === 'number') return value;
          if (typeof value === 'string') {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
          }
          return 0;
        });

        return {
          labels: rawResponse.results.rows.map(item => item[xaxisColumn]),
          datasets: [{
            label: yaxisLabel || 'Value',
            data: yValues,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1
          }],
          xaxisLabel: xaxisLabel || xaxisColumn,
          yaxisLabel: yaxisLabel || yaxisColumn,
          suggestedChart: interpretation.suggestedChart || 'line'
        };
      } catch (error) {
        console.error('Error generating chart data with interpretation:', error);
        return null;
      }
    }

    // Auto-detect columns if no interpretation
    if (interpretation?.suggestedChart && rawResponse?.results?.rows?.length > 0) {
      try {
        const firstRow = rawResponse.results.rows[0];
        const columns = Object.keys(firstRow);

        const dateColumns = columns.filter(col =>
          col.toLowerCase().includes('date') ||
          col.toLowerCase().includes('time') ||
          col.toLowerCase().includes('month') ||
          col.toLowerCase().includes('year')
        );

        const numericColumns = columns.filter(col => {
          const value = firstRow[col];
          return typeof value === 'number' ||
            (typeof value === 'string' && !isNaN(parseFloat(value)));
        });

        if (dateColumns.length === 0 || numericColumns.length === 0) {
          return null;
        }

        const xaxisColumn = dateColumns[0];
        const yaxisColumn = numericColumns[0];

        const yValues = rawResponse.results.rows.map(item => {
          const value = item[yaxisColumn];
          if (typeof value === 'number') return value;
          if (typeof value === 'string') {
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
          }
          return 0;
        });

        return {
          labels: rawResponse.results.rows.map(item => item[xaxisColumn]),
          datasets: [{
            label: yaxisColumn,
            data: yValues,
            borderColor: 'rgb(75, 192, 192)',
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            tension: 0.1
          }],
          xaxisLabel: xaxisColumn,
          yaxisLabel: yaxisColumn,
          suggestedChart: interpretation.suggestedChart
        };
      } catch (error) {
        console.error('Error generating chart data with auto-detected columns:', error);
        return null;
      }
    }

    return null;
  }, [rawResponse, hasChartData, showVisualizeData]);

  const chartOptions = useMemo(() => {
    const interpretation = rawResponse?.interpretation || rawResponse;
    const chartConfig = interpretation?.dimensions;

    if (!chartConfig && interpretation?.suggestedChart) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
          },
          title: {
            display: true,
            text: 'Data Visualization'
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'X Axis'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Y Axis'
            }
          }
        }
      };
    }

    if (!chartConfig) return null;

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: 'Data Visualization'
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: chartConfig.xaxisLabel || chartConfig.xaxisColumn
          },
          ticks: {
            maxRotation: 45,
            minRotation: 45
          }
        },
        y: {
          title: {
            display: true,
            text: chartConfig.yaxisLabel || chartConfig.yaxisColumn
          },
          beginAtZero: true
        }
      }
    };
  }, [rawResponse]);

  return {
    chartType,
    hasChartData,
    chartData,
    chartOptions
  };
};

export default useChartData;
